/**
 * RPC Provider Types
 * Defines interfaces for the RPC provider abstraction layer
 */

export type ProviderId = 'triton' | 'alchemy' | 'custom';

export interface HealthCheckResult {
  healthy: boolean;
  latency: number;
  timestamp: number;
  error?: string;
}

export interface RPCProvider {
  id: ProviderId;
  name: string;
  getHttpEndpoint(): string;
  getWsEndpoint(): string | undefined;
  healthCheck(): Promise<HealthCheckResult>;
  supportsWebhooks: boolean;
}

export interface ProviderStatus {
  id: ProviderId;
  name: string;
  healthy: boolean;
  latency: number;
  lastCheck: number;
  consecutiveFailures: number;
  supportsWebhooks: boolean;
}

export interface RPCProviderState {
  activeProvider: ProviderId;
  providers: Record<ProviderId, ProviderStatus>;
  userPreference: 'auto' | ProviderId;
  failoverCooldownUntil: number | null;
  isFailingOver: boolean;
}

export interface RPCProviderConfig {
  tritonRpcUrl?: string;
  tritonWsUrl?: string;
  alchemyRpcUrl?: string;
  customRpcUrl?: string;
  customWsUrl?: string;
}

// Failover configuration constants
export const FAILOVER_CONFIG = {
  /** Number of consecutive failures before failover */
  MAX_CONSECUTIVE_FAILURES: 3,
  /** Latency threshold in ms for sustained high latency failover */
  HIGH_LATENCY_THRESHOLD_MS: 1000,
  /** Duration in ms of sustained high latency before failover */
  HIGH_LATENCY_DURATION_MS: 60000,
  /** Cooldown period in ms after failover before switching back */
  FAILOVER_COOLDOWN_MS: 60000,
  /** Health check interval in ms */
  HEALTH_CHECK_INTERVAL_MS: 30000,
  /** WebSocket ping interval for Triton Whirligig */
  TRITON_WS_PING_INTERVAL_MS: 55000,
} as const;

// localStorage key for user preference
export const RPC_PREFERENCE_KEY = 'rpc_provider_preference';
